import React, { forwardRef, useEffect, useState } from "react";
import { Box, Stack, Typography, Divider, Card, CardMedia } from "@mui/material";
import { PreviewContainer, PreviewItem } from "../../../components";
import { format } from "date-fns";
import { useSelector } from "react-redux";
import { merchantApi } from "../../../redux/api/apiConfig";
import * as API from "../../../ApiUrl";
import pdfIcon from "../../../assets/img/pdfIcon.png";

const Preview = forwardRef(
  ({ formik, autoSelectedMto, recipientMtoCurrency, isPrint = false }, ref) => {
    const formData = formik.values;
    const user = useSelector((state) => state.profileReducer?.user);
    const [documentTypes, setDocumentTypes] = useState([]);
    
    // Get debtor details
    const debtor = formData.debtor;
    const debtorAccount = formData.debtor_account_details;
    
    // Get beneficiary details
    const beneficiary = formData.end_beneficiary;
    const beneficiaryAccount = formData.end_beneficiary_account;
    
    // Get current date/time for remittance date
    const remittanceDate = format(new Date(), "dd MMM yyyy HH:mm");
    
    // Get user name for "Created By"
    const createdByName = user?.name || user?.first_name && user?.last_name 
      ? `${user.first_name} ${user.last_name}` 
      : user?.email || "--";

    // Fetch document types to match with document type IDs
    useEffect(() => {
      const fetchDocumentTypes = async () => {
        try {
          const response = await merchantApi.get(API.BUCKZY_CODES, {
            params: {
              category: "DOCUMENT_TYPE",
            },
          });
          const documentTypesData = response.data?.data || [];
          const mappedTypes = documentTypesData.map((type) => ({
            code: type.code,
            description: type.description,
          }));
          setDocumentTypes(mappedTypes);
        } catch (error) {
          console.error("Error fetching document types:", error);
        }
      };
      fetchDocumentTypes();
    }, []);

    // Get document type name by code
    const getDocumentTypeName = (docTypeId) => {
      const docType = documentTypes.find((type) => type.code === docTypeId);
      return docType?.description || docTypeId || "--";
    };
    
    // Determine beneficiary name based on type
    const getBeneficiaryName = () => {
      if (!beneficiary) return "--";
      if (beneficiary.tp === "I") {
        return `${beneficiary.first_name || ""} ${beneficiary.last_name || ""}`.trim() || beneficiary.label || "--";
      } else if (beneficiary.tp === "B") {
        return beneficiary.business_name || beneficiary.label || "--";
      }
      return beneficiary.label || "--";
    };
    
    // Format exchange rate
    const formatExchangeRate = () => {
      if (!autoSelectedMto?.fees?.exchange_rate) return "--";
      return `1 ${formData.sender_currency || ""} = ${autoSelectedMto.fees.exchange_rate} ${formData.recipient_currency || ""}`;
    };
    
    // Format sender account display
    const formatSenderAccount = () => {
      if (!debtorAccount) return "--";
      const accountName = debtorAccount.nickName || `Account ${debtorAccount.accountId || ""}`;
      const balance = debtorAccount.balance || 0;
      const currency = debtorAccount.currencyCode || "";
      return `${accountName} (Balance: ${currency} ${balance.toFixed(2)})`;
    };
    
    const previewContent = (
      <Box ref={ref} sx={{ p: isPrint ? 2 : 0 }}>
        <Stack spacing={3}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            Transfer Preview
          </Typography>

          {/* Section 1: Remittance Details */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Remittance Details
            </Typography>
            <PreviewContainer>
              <PreviewItem
                title="Remittance Amount"
                content={`${formData.send_amount || 0} ${formData.sender_currency || ""}`}
              />
              <PreviewItem
                title="Receiver Gets"
                content={`${autoSelectedMto?.fees?.recipient_amount || 0} ${formData.recipient_currency || ""}`}
              />
              <PreviewItem
                title="Transaction Fee"
                content={`${autoSelectedMto?.fees?.transaction_fee || 0} ${formData.sender_currency || ""}`}
              />
              <PreviewItem
                title="Target Country"
                content={formData.target_country || "--"}
              />
              <PreviewItem
                title="Payment Method"
                content={formData.payment_method || "--"}
              />
              <PreviewItem
                title="Exchange Rate"
                content={formatExchangeRate()}
              />
              <PreviewItem
                title="Funding Source"
                content={formData.funding_source?.description || "--"}
              />
              <PreviewItem
                title="Date of Remittance"
                content={remittanceDate}
              />
              <PreviewItem
                title="Total Deposit Amount"
                content={`${autoSelectedMto?.fees?.user_pay || 0} ${formData.sender_currency || ""}`}
              />
              <PreviewItem
                title="Sender Account"
                content={formatSenderAccount()}
              />
              <PreviewItem
                title="Created By"
                content={createdByName}
              />
              {formData.payment_reference && (
                <PreviewItem
                  title="Payment Reference"
                  content={formData.payment_reference}
                />
              )}
            </PreviewContainer>
          </Box>

          <Divider />

          {/* Section 2: Sender Details */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Sender Details
            </Typography>
            <PreviewContainer>
              <PreviewItem
                title="Business Name"
                content={debtor?.business_name || debtor?.label || "--"}
              />
              {(debtor?.province || debtor?.buckzy_data?.address?.province) && (
                <PreviewItem
                  title="State"
                  content={debtor?.province || debtor?.buckzy_data?.address?.province || "--"}
                />
              )}
              {debtorAccount?.financialInstitutionDetails?.accountName && (
                <PreviewItem
                  title="Sender Name"
                  content={debtorAccount.financialInstitutionDetails.accountName || "--"}
                />
              )}
              {(debtor?.address || debtor?.buckzy_data?.address?.addressLine1) && (
                <PreviewItem
                  title="Address"
                  content={debtor?.address || debtor?.buckzy_data?.address?.addressLine1 || "--"}
                />
              )}
              {(debtor?.phone || debtor?.primary_contact_no) && (
                <PreviewItem
                  title="Phone"
                  content={debtor?.phone || debtor?.primary_contact_no || "--"}
                />
              )}
              {(debtor?.city || debtor?.buckzy_data?.address?.city) && (
                <PreviewItem
                  title="City"
                  content={debtor?.city || debtor?.buckzy_data?.address?.city || "--"}
                />
              )}
            </PreviewContainer>
          </Box>

          <Divider />

          {/* Section 3: Beneficiary Details */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Beneficiary Details
            </Typography>
            <PreviewContainer>
              {beneficiary?.tp === "B" ? (
                <PreviewItem
                  title="Business Name"
                  content={beneficiary.business_name || beneficiary.label || "--"}
                />
              ) : (
                <>
                  <PreviewItem
                    title="First Name"
                    content={beneficiary.first_name || "--"}
                  />
                  <PreviewItem
                    title="Last Name"
                    content={beneficiary.last_name || "--"}
                  />
                </>
              )}
              {beneficiary?.email && (
                <PreviewItem
                  title="Email"
                  content={beneficiary.email}
                />
              )}
              {beneficiary?.primary_contact_no && (
                <PreviewItem
                  title="Phone"
                  content={beneficiary.primary_contact_no}
                />
              )}
              {beneficiary?.tax_id && (
                <PreviewItem
                  title="Tax ID"
                  content={beneficiary.tax_id}
                />
              )}
              {beneficiary?.city && (
                <PreviewItem
                  title="City"
                  content={beneficiary.city}
                />
              )}
              {beneficiary?.province && (
                <PreviewItem
                  title="State"
                  content={beneficiary.province}
                />
              )}
              {beneficiary?.postal_code && (
                <PreviewItem
                  title="Postal Code"
                  content={beneficiary.postal_code}
                />
              )}
              {beneficiary?.address_line1 && (
                <PreviewItem
                  title="Address"
                  content={beneficiary.address_line1}
                />
              )}
              {beneficiary?.country_code && (
                <PreviewItem
                  title="Country"
                  content={beneficiary.country_code}
                />
              )}
            </PreviewContainer>
          </Box>

          <Divider />

          {/* Section 4: Financial Details */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Financial Details
            </Typography>
            <PreviewContainer>
              {beneficiaryAccount && (
                <>
                  <PreviewItem
                    title="Account Holder Name"
                    content={beneficiaryAccount.account_holder_name || "--"}
                  />
                  <PreviewItem
                    title="Account Number"
                    content={beneficiaryAccount.account_no || "--"}
                  />
                  <PreviewItem
                    title="Clearing Channel"
                    content={formData.payment_method || "--"}
                  />
                  {beneficiaryAccount.account_type_obj && (
                    <PreviewItem
                      title="Account Type"
                      content={beneficiaryAccount.account_type_obj.accountTypeName || "--"}
                    />
                  )}
                  <PreviewItem
                    title="Currency"
                    content={beneficiaryAccount.currency || "--"}
                  />
                  <PreviewItem
                    title="Country"
                    content={beneficiaryAccount.bank_country_code || "--"}
                  />
                  <PreviewItem
                    title="SWIFT BIC Code"
                    content={beneficiaryAccount.swift_code || "--"}
                  />
                  <PreviewItem
                    title="Bank Name"
                    content={beneficiaryAccount.bank_name || "--"}
                  />
                  <PreviewItem
                    title="Routing Number"
                    content={beneficiaryAccount.routing_number || "--"}
                  />
                </>
              )}
            </PreviewContainer>
          </Box>

          {/* Section 5: Documents */}
          {formData.sender_document && formData.sender_document.length > 0 && (
            <>
              <Divider />
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Documents
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat(auto-fill, minmax(150px, 1fr))",
                      sm: "repeat(auto-fill, minmax(180px, 1fr))",
                      md: "repeat(auto-fill, minmax(200px, 1fr))",
                    },
                    gap: 2,
                    mt: 2,
                  }}
                >
                  {formData.sender_document.map((doc, index) => (
                    <DocumentPreviewCard
                      key={index}
                      document={doc}
                      documentTypeName={getDocumentTypeName(doc.doc_type_id)}
                    />
                  ))}
                </Box>
              </Box>
            </>
          )}

          {isPrint && (
            <Box sx={{ mt: 4, textAlign: "center" }}>
              <Typography variant="caption" color="text.secondary">
                Generated on {format(new Date(), "dd MMM yyyy HH:mm")}
              </Typography>
            </Box>
          )}
        </Stack>
      </Box>
    );

    return previewContent;
  }
);

Preview.displayName = "Preview";

// Document Preview Card Component
function DocumentPreviewCard({ document, documentTypeName }) {
  const [isImage, setIsImage] = useState(false);
  const [imgUrl, setImgUrl] = useState(document?.url || document?.preview);

  useEffect(() => {
    if (document?.url) {
      const checkIsImage = async () => {
        try {
          const response = await fetch(document.url);
          if (!response.ok) {
            throw new Error(
              `Failed to fetch the file: ${response.status} ${response.statusText}`
            );
          }

          const blob = await response.blob();
          const mimeType = blob.type;
          
          if (
            mimeType === "image/png" ||
            mimeType === "image/jpg" ||
            mimeType === "image/jpeg" ||
            mimeType === "image/svg+xml" ||
            mimeType === "image/gif" ||
            mimeType === "image/webp"
          ) {
            setIsImage(true);
            setImgUrl(document.url);
          } else {
            setIsImage(false);
            setImgUrl(pdfIcon);
          }
        } catch (error) {
          console.error("Error checking file type:", error.message);
          // Fallback: check by URL extension
          if (document.url && !document.url.toLowerCase().endsWith(".pdf")) {
            setIsImage(true);
            setImgUrl(document.url);
          } else {
            setIsImage(false);
            setImgUrl(pdfIcon);
          }
        }
      };
      checkIsImage();
    } else if (document?.preview) {
      setIsImage(true);
      setImgUrl(document.preview);
    }
  }, [document?.url, document?.preview]);

  return (
    <Card
      sx={{
        position: "relative",
        cursor: "pointer",
        "&:hover": {
          boxShadow: 4,
        },
      }}
      onClick={() => {
        if (document?.url) {
          window.open(document.url, "_blank");
        }
      }}
    >
      <CardMedia
        component="img"
        height="140"
        image={imgUrl}
        alt={document?.name || "Document"}
        sx={{
          objectFit: isImage ? "cover" : "contain",
          bgcolor: isImage ? "transparent" : "grey.100",
        }}
      />
      <Box sx={{ p: 1 }}>
        <Typography variant="caption" display="block" noWrap>
          {document?.name || "Document"}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" noWrap>
          {documentTypeName}
        </Typography>
      </Box>
    </Card>
  );
}

export default Preview;
